<template>
    <div>
        <!-- 隐藏的答题卡容器，用于生成PDF -->
        <div ref="answerCardRef" class="answer_card" :style="cardStyles">
            <!-- 答题卡头部 -->
            <div class="card_header">
                <h2 class="card_title">答题卡</h2>
                <div class="card_subtitle">{{ paperContent.paperName }}</div>
                <div class="student_info">
                    <div class="info_item">姓名：________________</div>
                    <div class="info_item">班级：________________</div>
                    <div class="info_item">准考证号：________________</div>
                </div>
                <div class="fill_instruction">
                    <div class="fill_example">
                        <span class="example_title">填涂样例：</span>
                        <span class="example_box correct">■</span>
                        <span class="example_text">正确填涂</span>
                        <span class="example_box wrong">[ ]</span>
                        <span class="example_text">错误填涂</span>
                    </div>
                    <div class="fill_notice">请用2B铅笔填涂，修改时请用橡皮擦干净</div>
                </div>
            </div>

            <!-- 客观题区域（单选、多选、判断） -->
            <div class="objective_section" v-for="(section, idx) in objectiveSections" :key="'obj' + idx">
                <div class="section_title">{{ getSectionNumber(idx) }}{{ section.title }}</div>
                <div class="question_grid" :class="{ 'multi_col': section.type === 'choice' }">
                    <template v-for="(question, qIdx) in section.questions" :key="qIdx">
                        <div class="question_row">
                            <div class="question_number">{{ question.questionNumber }}.</div>
                            <div class="answer_options" :class="getOptionClass(section.type)">
                                <template v-if="section.type === 'choice' || section.type === 'judge'">
                                    <!-- 单选题/判断题：ABCD或√× -->
                                    <span v-for="opt in getOptions(section.type, question.questionContext?.options)"
                                        :key="opt" class="option_item">
                                        [{{ opt }}]
                                    </span>
                                </template>
                                <template v-else-if="section.type === 'multiple'">
                                    <!-- 多选题：每个选项独立 -->
                                    <span v-for="opt in getOptions(section.type, question.questionContext?.options)"
                                        :key="opt" class="option_item multi_option">
                                        [{{ opt }}]
                                    </span>
                                </template>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- 主观题区域（填空、排序、文本解答、完形填空、综合解答、作文） -->
            <!-- 主观题区域（包含有子题的题型） -->
            <div class="subjective_section" v-for="(section, idx) in subjectiveSections" :key="'sub' + idx">
                <div class="section_title">{{ getSectionNumber(objectiveSections.length + idx) }}{{ section.title }}
                </div>
                <div class="subjective_items">
                    <div v-for="(question, qIdx) in section.questions" :key="qIdx" class="subjective_item">
                        <!-- 母题题号 -->
                        <div class="question_number">{{ question.questionNumber }}.</div>

                        <!-- 答题区域 -->
                        <div class="answer_area" :class="getAnswerAreaClass(section.type)">

                            <!-- 情况1：有子题的题型（完形填空、综合题） -->
                            <template v-if="hasSubQuestions(question)">
                                <div class="sub_questions_container">
                                    <!-- 动态渲染子题 -->
                                    <div v-for="(subQ, subIdx) in question.questionContext.subQuestions" :key="subIdx"
                                        class="sub_question_item">
                                        <!-- 根据子题类型渲染不同区域 -->
                                        <template
                                            v-if="['choice', 'multiple', 'judge'].includes(getSubQuestionType(subQ.type))">
                                            <!-- 客观题子题：横向排列 -->
                                            <div class="sub_question_row">
                                                <span class="sub_number">{{ question.questionNumber }}.{{ subIdx + 1
                                                }}</span>
                                                <div class="answer_options"
                                                    :class="getOptionClass(getSubQuestionType(subQ.type))">
                                                    <template v-if="getSubQuestionType(subQ.type) === 'choice'">
                                                        <span v-for="opt in getOptions('choice', subQ?.options || [])"
                                                            :key="opt" class="option_item">[{{ opt }}]</span>
                                                    </template>

                                                    <template v-else-if="getSubQuestionType(subQ.type) === 'multiple'">
                                                        <span v-for="opt in getOptions('multiple', subQ?.options || [])"
                                                            :key="opt" class="option_item">[{{ opt }}]</span>
                                                    </template>

                                                    <template v-else-if="getSubQuestionType(subQ.type) === 'judge'">
                                                        <span class="option_item">[√]</span>
                                                        <span class="option_item">[×]</span>
                                                    </template>
                                                </div>
                                            </div>
                                        </template>

                                        <template v-else-if="getSubQuestionType(subQ.type) === 'fill'">
                                            <!-- 填空题子题 -->
                                            <div class="sub_question_row">
                                                <span class="sub_number">{{ question.questionNumber }}.{{ subIdx + 1
                                                }}</span>
                                                <div class="fill_area">
                                                    <span v-for="i in (subQ?.options?.length || 3)" :key="i"
                                                        class="fill_blank">( {{ i }} )________</span>
                                                </div>
                                            </div>
                                        </template>

                                        <template v-else-if="getSubQuestionType(subQ.type) === 'sort'">
                                            <!-- 排序题子题 -->
                                            <div class="sub_question_row">
                                                <span class="sub_number">{{ question.questionNumber }}.{{ subIdx + 1
                                                }}</span>
                                                <div class="sort_area">
                                                    <span v-for="(opt, optIdx) in (subQ?.options || Array(4).fill(''))"
                                                        :key="optIdx" class="sort_item">( ) {{ optIdx <
                                                            (subQ?.options?.length - 1) ? '→' : '' }}</span>
                                                </div>
                                            </div>
                                        </template>

                                        <template v-else-if="getSubQuestionType(subQ.type) === 'text'">
                                            <!-- 文本解答子题 -->
                                            <div class="sub_question_row">
                                                <span class="sub_number">{{ question.questionNumber }}.{{ subIdx + 1
                                                }}</span>
                                                <div class="text_area">
                                                    <div v-for="i in 2" :key="i" class="text_line"></div>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </template>

                            <!-- 情况2：无子题的普通题型（保持原有逻辑） -->
                            <template v-else>
                                <!-- 填空题 -->
                                <template v-if="section.type === 'fill'">
                                    <span v-for="i in (question.questionContext?.options?.length || 3)" :key="i"
                                        class="fill_blank">
                                        ({{ i }})________________
                                    </span>
                                </template>

                                <!-- 排序题 -->
                                <template v-else-if="section.type === 'sort'">
                                    <span class="sort_area"
                                        v-for="i in (question.questionContext?.options?.length || 4)" :key="i">
                                        ( ) {{ i < (question.questionContext?.options?.length || 4) ? '→' : '' }}
                                            </span>
                                </template>

                                <!-- 文本解答 -->
                                <template v-else-if="section.type === 'text'">
                                    <div class="text_lines">
                                        <div v-for="i in 3" :key="i" class="text_line"></div>
                                    </div>
                                </template>

                                <!-- 作文题 -->
                                <template v-else-if="section.type === 'composition'">
                                    <div class="composition_area">
                                        <div class="text_lines">
                                            <div v-for="i in 30" :key="i" class="text_line"></div>
                                        </div>
                                        <div class="word_count">字数：______字</div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 总分区域 -->
            <!-- <div class="total_score_section" v-if="showTotalScore">
                <div class="total_score_title">总分统计</div>
                <div class="total_score_table">
                    <table>
                        <thead>
                            <tr>
                                <th>题型</th>
                                <th>得分</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(section, idx) in allSections" :key="idx">
                                <td>{{ section.title }}</td>
                                <td></td>
                            </tr>
                            <tr class="total_row">
                                <td>总分</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div> -->
        </div>
    </div>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default {
    name: 'AnswerCardDownload',
    props: {
        paperConfig: {
            type: Object,
            default: () => ({})
        },
        paperContent: {
            type: Object,
            required: true
        },
        paperTitle: {
            type: String,
            default: '试卷答题卡'
        },
        showTotalScore: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            cardStyles: {
                width: '210mm',
                padding: '10mm',
                backgroundColor: 'white',
                fontFamily: 'SimSun, "宋体", serif'
            }
        };
    },

    computed: {
        // 按题型分类题目，并保持试卷原始顺序
        allSections() {
            const sectionMap = new Map(); // 用于合并相同题型
            const typeMap = {
                1: { title: '单项选择题', type: 'choice' },
                2: { title: '多项选择题', type: 'multiple' },
                4: { title: '判断题', type: 'judge' },
                3: { title: '填空题', type: 'fill' },
                6: { title: '排序题', type: 'sort' },
                7: { title: '简答题', type: 'text' },
                99: { title: '完形填空', type: 'cloze' },
                101: { title: '综合题', type: 'comprehensive' },
                21: { title: '作文题', type: 'composition' },
                9: { title: '听力单选题', type: 'choice' },
                11: { title: '听力判断题', type: 'judge' },
                14: { title: '听力填空题', type: 'fill' },
                15: { title: '听力排序题', type: 'sort' },
                13: { title: '简答题', type: 'text' },
                16: { title: '简答题', type: 'text' },
                17: { title: '简答题', type: 'text' },
                32: { title: '信息匹配题', type: 'match' },
                102: { title: '综合题', type: 'cloze' },
                103: { title: '综合题', type: 'comprehensive' },
            };

            // 用于记录题型出现的顺序
            const orderList = [];

            this.paperContent.paperContext?.forEach(item => {
                const typeInfo = typeMap[item.questionTypeId] || {
                    title: item.questionTypeName || '其他题型',
                    type: 'other'
                };

                if (item.questions?.length === 0) return;

                // 使用题型ID作为key（因为不同ID可能有相同title）
                const key = `${item.questionTypeId}`;

                if (!sectionMap.has(key)) {
                    // 新题型，记录顺序
                    orderList.push({
                        key,
                        title: typeInfo.title,
                        type: typeInfo.type,
                        questionTypeId: item.questionTypeId // 保留原始题型ID
                    });

                    sectionMap.set(key, {
                        title: typeInfo.title,
                        type: typeInfo.type,
                        questionTypeId: item.questionTypeId,
                        questions: [...item.questions]
                    });
                } else {
                    // 已存在的题型，合并题目
                    const existingSection = sectionMap.get(key);
                    existingSection.questions.push(...item.questions);
                }
            });

            // 按照试卷原始顺序构建sections
            const sections = orderList.map(orderItem =>
                sectionMap.get(orderItem.key)
            );

            console.log('合并后的题型顺序:', sections.map(s => s.title));
            return sections;
        },

        // 客观题（单选、多选、判断）
        objectiveSections() {
            return this.allSections.filter(s =>
                ['choice', 'multiple', 'judge'].includes(s.type)
            );
        },

        // 主观题（其他）- 保持原有顺序
        subjectiveSections() {
            return this.allSections.filter(s =>
                !['choice', 'multiple', 'judge'].includes(s.type)
            );
        }
    },

    methods: {
        // 根据索引生成中文序号
        getSectionNumber(index) {
            const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十'];
            return chineseNumbers[index] ? chineseNumbers[index] + '、' : '';
        },
        // 判断题型是否有子题
        hasSubQuestions(question) {
            return question.questionContext?.subQuestions &&
                question.questionContext.subQuestions.length > 0;
        },

        // 获取子题的题型类型
        getSubQuestionType(typeId) {
            const typeMap = {
                1: 'choice',
                2: 'multiple',
                4: 'judge',
                3: 'fill',
                6: 'sort',
                7: 'text',
                9: 'choice',    // 听力单选
                11: 'judge',     // 听力判断
                14: 'fill',      // 听力填空
                15: 'sort',      // 听力排序
            };
            return typeMap[typeId] || 'text';
        },

        // 渲染子题的答题区域
        renderSubQuestionArea(question) {
            const subQuestions = question.questionContext?.subQuestions || [];

            return subQuestions.map((subQ, subIndex) => {
                const subType = this.getSubQuestionType(subQ.type);
                const subNumber = `${question.questionNumber}.${subIndex + 1}`;

                return this.renderAnswerAreaByType(subType, subQ, subNumber);
            }).join('');
        },

        // 根据题型渲染答题区域（返回HTML字符串或VNode）
        renderAnswerAreaByType(type, question, displayNumber) {
            const ctx = question.questionContext || {};
            const options = ctx.options || [];

            switch (type) {
                case 'choice': // 单选题
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="answer_options choice_options">
              ${this.getOptions('choice', options).map(opt =>
                        `<span class="option_item">[${opt}]</span>`
                    ).join('')}
            </div>
          </div>
        `;

                case 'multiple': // 多选题
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="answer_options multi_options">
              ${this.getOptions('multiple', options).map(opt =>
                        `<span class="option_item">[${opt}]</span>`
                    ).join('')}
            </div>
          </div>
        `;

                case 'judge': // 判断题
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="answer_options judge_options">
              <span class="option_item">[√]</span>
              <span class="option_item">[×]</span>
            </div>
          </div>
        `;

                case 'fill': // 填空题
                    // eslint-disable-next-line no-case-declarations
                    const blankCount = ctx.options?.length || 3;
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="fill_area">
              ${Array(blankCount).fill(0).map((_, i) =>
                        `<span class="fill_blank">( ${i + 1} )________</span>`
                    ).join('')}
            </div>
          </div>
        `;

                case 'sort': // 排序题
                    // eslint-disable-next-line no-case-declarations
                    const sortCount = ctx.options?.length || 4;
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="sort_area">
              ${Array(sortCount).fill(0).map((_, i) =>
                        `<span class="sort_item">(   ) ${i < sortCount - 1 ? '→' : ''}</span>`
                    ).join('')}
            </div>
          </div>
        `;

                case 'text': // 文本解答
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="text_area">
              <div class="text_line"></div>
              <div class="text_line"></div>
              <div class="text_line"></div>
            </div>
          </div>
        `;

                default:
                    return `
          <div class="sub_question_row">
            <span class="sub_number">${displayNumber}</span>
            <div class="default_area">______________</div>
          </div>
        `;
            }
        },

        // 获取选项（保持原有逻辑）
        getOptions(type, options) {
            if (type === 'judge') return ['√', '×'];
            return options.map(item => item.label);
        },

        getOptionClass(type) {
            if (type === 'multiple') return 'multi_options';
            if (type === 'judge') return 'judge_options';
            return 'choice_options';
        },

        getAnswerAreaClass(type) {
            const map = {
                fill: 'fill_area',
                sort: 'sort_area',
                text: 'text_area',
                cloze: 'cloze_area',
                comprehensive: 'comprehensive_area',
                composition: 'composition_area'
            };
            return map[type] || 'default_area';
        },

        async downloadAnswerCard() {
            const element = this.$refs.answerCardRef;
            if (!element) return;

            try {
                const canvas = await html2canvas(element, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });

                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'mm',
                    format: 'a4'
                });

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const margin = 10;
                const imgWidth = pdfWidth - margin * 2;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                // 如果内容超长，分页
                if (imgHeight <= pdfHeight - margin * 2) {
                    const imgData = canvas.toDataURL('image/jpeg', 0.95);
                    pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
                } else {
                    const totalPages = Math.ceil(imgHeight / (pdfHeight - margin * 2));

                    for (let i = 0; i < totalPages; i++) {
                        if (i > 0) pdf.addPage();

                        const yOffset = i * (pdfHeight - margin * 2) * (canvas.width / imgWidth);
                        const pageHeight = Math.min(
                            canvas.height - yOffset,
                            (pdfHeight - margin * 2) * (canvas.width / imgWidth)
                        );

                        const pageCanvas = document.createElement('canvas');
                        pageCanvas.width = canvas.width;
                        pageCanvas.height = pageHeight;
                        const ctx = pageCanvas.getContext('2d');
                        ctx.drawImage(
                            canvas,
                            0, yOffset, canvas.width, pageHeight,
                            0, 0, pageCanvas.width, pageHeight
                        );

                        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
                        const pageImgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;

                        pdf.addImage(pageImgData, 'JPEG', margin, margin, imgWidth, pageImgHeight);

                        // 添加页码
                        pdf.setPage(i + 1);
                        pdf.setFontSize(10);
                        pdf.setTextColor(100);
                        pdf.text(`Page ${totalPages} `, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
                    }
                }

                pdf.save('答题卡.pdf');
                return true;
            } catch (error) {
                console.error('答题卡生成失败:', error);
                throw error;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.answer_card {
    font-size: 12px;
    line-height: 1.5;
    color: #333;

    .card_header {
        text-align: center;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 2px solid #333;

        .card_title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
        }

        .card_subtitle {
            font-size: 14px;
            margin-bottom: 15px;
        }

        .student_info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;

            .info_item {
                font-size: 14px;
            }
        }

        .fill_instruction {
            text-align: left;
            background: #f5f5f5;
            padding: 8px;
            border-radius: 4px;

            .fill_example {
                display: flex;
                align-items: center;
                gap: 5px;

                .example_box {
                    font-size: 16px;

                    &.correct {
                        color: #333;
                    }

                    &.wrong {
                        color: #999;
                    }
                }
            }

            .fill_notice {
                color: #666;
                margin-top: 4px;
            }
        }
    }

    .section_title {
        font-size: 14px;
        font-weight: bold;
        margin: 15px 0 10px;
        padding-left: 5px;
        border-left: 4px solid #1FD294;
    }

    // 客观题样式
    .question_grid {
        display: flex;
        flex-wrap: wrap;
        gap: 10px 20px;

        &.multi_col {
            .question_row {
                width: calc(50% - 10px);
            }
        }
    }

    .question_row {
        display: flex;
        align-items: baseline;
        margin-bottom: 8px;

        .question_number {
            width: 30px;
            font-weight: bold;
        }

        .answer_options {
            display: flex;
            gap: 15px;

            &.choice_options {
                .option_item {
                    width: 30px;
                }
            }

            &.multi_options {
                .option_item {
                    margin-right: 8px;
                }
            }

            &.judge_options {
                .option_item {
                    width: 30px;
                }
            }
        }
    }

    // 主观题样式
    .subjective_items {
        .subjective_item {
            display: flex;
            margin-bottom: 15px;

            .question_number {
                width: 40px;
                font-weight: bold;
            }

            .answer_area {
                flex: 1;

                .fill_blank {
                    display: inline-block;
                    margin-right: 20px;
                    //   border-bottom: 1px solid #333;
                    min-width: 80px;
                    text-align: center;
                }

                .text_line {
                    border-bottom: 1px solid #333;
                    margin-bottom: 5px;
                    min-height: 20px;
                }

                .cloze_options {
                    .cloze_item {
                        display: inline-block;
                        margin-right: 20px;
                        margin-bottom: 5px;
                    }
                }

                .composition_line {
                    border-bottom: 1px dashed #999;
                    margin-bottom: 8px;
                    min-height: 20px;
                }

                .word_count {
                    text-align: right;
                    margin-top: 10px;
                }
            }
        }
    }

    .sub_questions_container {
        .sub_question_item {
            margin-bottom: 10px;

            .sub_question_row {
                display: flex;
                align-items: anchor-center;
                margin-bottom: 8px;

                .sub_number {
                    width: 50px;
                    font-size: 11px;
                    color: #666;
                    margin-right: 5px;
                }

                .answer_options {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px 15px;
                }

                .fill_area {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;

                    .fill_blank {
                        min-width: 80px;
                    }
                }

                .sort_area {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 5px;

                    .sort_item {
                        display: inline-flex;
                        align-items: center;
                    }
                }

                .text_area {
                    flex: 1;

                    .text_line {
                        border-bottom: 1px solid #333;
                        margin-bottom: 5px;
                        min-height: 18px;
                        width: 100%;
                    }
                }
            }
        }
    }

    // 子题缩进
    .subjective_item {
        .answer_area {
            .sub_questions_container {
                margin-left: 10px;
                border-left: 1px dashed #ddd;
                padding-left: 10px;
            }
        }
    }

    // 作文题样式
    .composition_area {
        width: 100%;
        margin-top: 5px;

        .composition_lines {
            border: 1px solid #e0e0e0;
            padding: 10px;

            .composition_row {
                display: flex;
                align-items: center;
                margin-bottom: 6px;
                font-size: 14px;

                .row_number {
                    width: 30px;
                    color: #666;
                    font-size: 12px;
                }

                .underline_line {
                    flex: 1;
                    display: flex;
                    flex-wrap: wrap;
                    letter-spacing: 2px;
                    border-bottom: 1px solid #333;
                    min-height: 24px;

                    .underline_char {
                        color: #333;
                        font-size: 14px;
                        opacity: 0.5;
                    }
                }
            }
        }

        .word_count {
            text-align: right;
            margin-top: 10px;
            font-size: 12px;
            color: #666;
            padding-right: 10px;
        }

        // 方格作文样式
        .composition_grid {
            border: 1px solid #e0e0e0;
            padding: 15px;

            .grid_row {
                display: flex;
                align-items: center;
                margin-bottom: 4px;

                .row_number {
                    width: 30px;
                    font-size: 12px;
                    color: #999;
                }

                .grid_cell {
                    width: 20px;
                    height: 20px;
                    border: 1px solid #ccc;
                    display: inline-block;
                    margin-right: 2px;
                    position: relative;

                    // 可以添加轻微的网格线效果
                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(45deg, transparent 48%, #ddd 48%, #ddd 52%, transparent 52%);
                        opacity: 0.3;
                    }
                }
            }
        }
    }

    // 总分区域
    .total_score_section {
        margin-top: 30px;
        padding-top: 20px;
        border-top: 2px dashed #999;

        .total_score_title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
        }

        table {
            width: 50%;
            border-collapse: collapse;

            th,
            td {
                border: 1px solid #333;
                padding: 8px;
                text-align: center;
            }

            th {
                background: #f5f5f5;
                font-weight: normal;
            }

            .total_row {
                font-weight: bold;
                background: #f0f0f0;
            }
        }
    }
}
</style>